import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mail, ExternalLink, RefreshCw, Loader, CheckCircle, Clock } from 'lucide-react';
import {
    sendVerificationEmail,
    getEmailVerificationData,
    checkUserVerificationState,
    changeStepToNextAfterEmailVerification,
} from '../../../services/verificationService';
import { isFirebaseEmailVerified } from '../../../services/FirebaseService';

const RESEND_COOLDOWN = 60; // seconds
const AUTO_SEND_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes — minimum time between auto-sends

// ── Global lock: only ONE send can be in-flight at a time across all instances ──
let _globalSending = false;

// ── localStorage key for tracking last auto-send time ──
const emailSentKey = (uid) => `__daisies_email_sent_ts_${uid}`;

const EmailVerification = ({ userId, userEmail, onVerified }) => {
    const [loading, setLoading] = useState(true);
    const [verificationLink, setVerificationLink] = useState(null);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [error, setError] = useState('');
    const [emailSent, setEmailSent] = useState(false);
    const [verified, setVerified] = useState(false);
    const [checking, setChecking] = useState(false);
    const cooldownRef = useRef(null);
    const mountedRef = useRef(true);

    // Mask email for display: r****o@gmail.com
    const maskEmail = (email) => {
        if (!email) return '';
        const [local, domain] = email.split('@');
        if (local.length <= 2) return `${local[0]}***@${domain}`;
        return `${local[0]}${'*'.repeat(Math.min(local.length - 2, 4))}${local[local.length - 1]}@${domain}`;
    };

    // Check if we recently auto-sent an email (survives page refreshes & re-mounts)
    const wasRecentlySent = useCallback(() => {
        try {
            const ts = localStorage.getItem(emailSentKey(userId));
            if (!ts) return false;
            return Date.now() - parseInt(ts, 10) < AUTO_SEND_COOLDOWN_MS;
        } catch {
            return false;
        }
    }, [userId]);

    // Mark that we just auto-sent
    const markSent = useCallback(() => {
        try {
            localStorage.setItem(emailSentKey(userId), String(Date.now()));
        } catch { /* swallow */ }
    }, [userId]);

    // Send initial verification email — ONLY ONCE per cooldown window
    const initVerification = useCallback(async () => {
        // Guard 1: recently sent (persists across page reloads & re-mounts)
        if (wasRecentlySent()) {
            // Already sent recently — just fetch the link so the UI is usable
            setLoading(true);
            try {
                const linkResult = await getEmailVerificationData(userId);
                if (mountedRef.current && linkResult.success) {
                    setVerificationLink(linkResult.verificationLink);
                }
            } catch { /* swallow */ }
            if (mountedRef.current) {
                setEmailSent(true);
                setLoading(false);
            }
            return;
        }

        // Guard 2: another instance is already sending right now
        if (_globalSending) {
            if (mountedRef.current) setLoading(false);
            return;
        }
        _globalSending = true;

        setLoading(true);
        setError('');
        try {
            // Send the verification email ONCE
            const sendResult = await sendVerificationEmail(userId);
            if (!sendResult.success) {
                console.warn('send_verify_email:', sendResult.error);
            }

            // Mark as sent BEFORE anything else
            markSent();

            // Get the verification link
            const linkResult = await getEmailVerificationData(userId);
            if (mountedRef.current && linkResult.success) {
                setVerificationLink(linkResult.verificationLink);
            }

            if (mountedRef.current) {
                setEmailSent(true);
                setResendCooldown(RESEND_COOLDOWN);
            }
        } catch (err) {
            if (mountedRef.current) {
                setError('Error al enviar el correo de verificación. Intenta de nuevo.');
            }
            // Do NOT mark as sent on error so user can retry
        } finally {
            _globalSending = false;
            if (mountedRef.current) setLoading(false);
        }
    }, [userId, wasRecentlySent, markSent]);

    // Cleanup mounted ref
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // Resend cooldown timer
    useEffect(() => {
        if (resendCooldown <= 0) {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
            return;
        }

        cooldownRef.current = setInterval(() => {
            setResendCooldown(prev => {
                if (prev <= 1) {
                    clearInterval(cooldownRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
        };
    }, [resendCooldown]);

    // Init on mount
    useEffect(() => {
        initVerification();
    }, [initVerification]);

    // Resend handler — the ONLY way to send another email manually
    const handleResend = async () => {
        if (resendCooldown > 0 || sendingEmail || _globalSending) return;
        setSendingEmail(true);
        _globalSending = true;
        setError('');

        try {
            const sendResult = await sendVerificationEmail(userId);
            if (!sendResult.success) {
                setError(sendResult.error || 'Error al reenviar el correo');
            } else {
                setEmailSent(true);
                // Update send timestamp
                markSent();
            }

            const linkResult = await getEmailVerificationData(userId);
            if (linkResult.success) {
                setVerificationLink(linkResult.verificationLink);
            }

            setResendCooldown(RESEND_COOLDOWN);
        } catch {
            setError('Error al reenviar. Intenta de nuevo.');
        } finally {
            _globalSending = false;
            setSendingEmail(false);
        }
    };

    // Open verification link
    const handleOpenLink = () => {
        if (verificationLink) {
            window.open(verificationLink, '_blank');
        }
    };

    // Manual check — ONLY validates, does NOT force-transition
    // 1. Call validate_currently_user_state to get live step_state from Odoo
    // 2. Si el step_state ya no es ninguno de los estados pendientes de email → verificado
    // 3. Si sigue pendiente → mostrar error (el correo aún no se verificó)
    const PENDING_EMAIL_STATES = new Set([
        'pending_verification_email',
        'pending_email_verification',
        'pending_email',
        'email_pending',
    ]);

    const handleManualCheck = async () => {
        setChecking(true);
        setError('');
        try {
            // Flujo correcto (mirroring de flutterUXS/lib/pages/email_validation_page):
            // 1. Source of truth: Firebase Auth. Recargar el currentUser y leer emailVerified.
            //    NO confiar en `change_step_pending_email_verification_to_next_step` directamente
            //    porque ese endpoint avanza el step SIN validar — dejaba pasar a verif. de teléfono
            //    aunque el correo nunca se hubiera verificado (bug reportado 2026-05-06).
            // 2. Si Firebase dice verified=true, leer step_state de Odoo.
            //    - Si ya avanzó (step_state != pending_verification_email) → marcar verificado.
            //    - Si sigue pendiente → llamar a change_step para que Odoo lo avance.
            // 3. Si Firebase dice verified=false → mostrar error claro, NO avanzar.
            const fbCheck = await isFirebaseEmailVerified();
            console.log('[EmailVerification] Firebase emailVerified check:', fbCheck);

            if (!fbCheck.verified) {
                setError(
                    fbCheck.error
                        ? `No pudimos confirmar la verificación: ${fbCheck.error}`
                        : 'Tu correo aún no ha sido verificado. Abre el enlace que te enviamos y vuelve a intentarlo.'
                );
                return;
            }

            // Firebase confirmó: el correo está verificado.
            // Leer el step actual de Odoo para decidir si llamar a change_step.
            const stateResult = await checkUserVerificationState(userId);
            console.log('[EmailVerification] Odoo step_state:', stateResult);

            const state = stateResult.success && stateResult.state ? String(stateResult.state).toLowerCase() : '';
            const stillPending = !state || PENDING_EMAIL_STATES.has(state);

            if (stillPending) {
                // Pedirle a Odoo que avance el step ahora que sabemos que Firebase está OK
                const advanceResult = await changeStepToNextAfterEmailVerification(userId);
                console.log('[EmailVerification] advance step result:', advanceResult);
                // Si advance falla pero Firebase confirmó, igual seguimos:
                // el state_state podría tardar en propagarse. El siguiente refreshUserData
                // lo capturará desde Firestore.
            }

            try { localStorage.removeItem(emailSentKey(userId)); } catch { /* swallow */ }
            setVerified(true);
            setTimeout(() => {
                if (onVerified) onVerified();
            }, 1500);
        } catch (err) {
            console.warn('[EmailVerification] Error en handleManualCheck:', err);
            setError('Error al verificar el estado. Intenta de nuevo.');
        } finally {
            setChecking(false);
        }
    };

    // Force resend — permite al usuario forzar el reenvío incluso si hay cooldown,
    // por si el correo no llegó (spam, error transitorio, etc.)
    const handleForceResend = async () => {
        if (sendingEmail || _globalSending) return;
        // Limpiar el cooldown persistente para destrabar futuros envíos automáticos
        try { localStorage.removeItem(emailSentKey(userId)); } catch { /* swallow */ }
        setResendCooldown(0);
        await handleResend();
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-16">
                <Loader className="animate-spin text-daisies mb-4" size={40} />
                <p className="text-text-body font-medium">Enviando correo de verificación...</p>
            </div>
        );
    }

    if (verified) {
        return (
            <div className="flex flex-col items-center justify-center py-16 animate-fadeIn">
                <div className="w-20 h-20 bg-mint-pill rounded-full flex items-center justify-center mb-6">
                    <CheckCircle className="text-mint-deep" size={48} />
                </div>
                <h3 className="text-2xl font-bold text-text-primary mb-2">¡Correo verificado!</h3>
                <p className="text-text-body">Tu dirección de correo ha sido verificada exitosamente.</p>
                <p className="text-sm text-text-mute mt-2">Continuando al siguiente paso...</p>
            </div>
        );
    }

    return (
        <div className="animate-fadeIn">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="w-20 h-20 bg-daisies/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Mail className="text-daisies" size={40} />
                </div>
                <h3 className="text-2xl font-bold text-text-primary mb-2">Verifica tu correo electrónico</h3>
                <p className="text-text-body">
                    Hemos enviado un enlace de verificación a
                </p>
                <p className="text-daisies font-semibold mt-1 text-lg">{maskEmail(userEmail)}</p>
            </div>

            {/* Instructions */}
            <div className="bg-brand-tint rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                    <div className="bg-white p-2 rounded-lg flex-shrink-0 mt-0.5">
                        <Mail className="text-brand" size={18} />
                    </div>
                    <div className="text-sm text-brand-deep">
                        <p className="font-semibold mb-1">¿Cómo verificar?</p>
                        <ol className="list-decimal list-inside space-y-1 text-brand-deep">
                            <li>Haz clic en el botón de abajo para abrir el enlace de verificación</li>
                            <li>Se abrirá una nueva pestaña en tu navegador</li>
                            <li>Regresa aquí y haz clic en "Ya verifiqué mi correo"</li>
                        </ol>
                    </div>
                </div>
            </div>

            {/* Open link button */}
            {verificationLink && (
                <button
                    onClick={handleOpenLink}
                    className="w-full py-4 bg-daisies text-white rounded-card font-bold text-lg hover:bg-daisies-hover transition-all flex items-center justify-center gap-3 mb-4"
                >
                    <ExternalLink size={22} />
                    Abrir enlace de verificación
                </button>
            )}

            {/* Manual check button */}
            <button
                onClick={handleManualCheck}
                disabled={checking}
                className="w-full py-3 bg-mint-pill text-mint-deep rounded-xl font-semibold hover:bg-mint-pill/80 transition-all flex items-center justify-center gap-2 mb-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {checking ? (
                    <>
                        <Loader className="animate-spin" size={18} />
                        Verificando...
                    </>
                ) : (
                    <>
                        <CheckCircle size={18} />
                        Ya verifiqué mi correo
                    </>
                )}
            </button>

            {/* Error message */}
            {error && (
                <div className="bg-red-50 rounded-xl p-4 mb-4">
                    <p className="text-sm text-red-700">{error}</p>
                </div>
            )}

            {/* Resend button */}
            <div className="text-center">
                <p className="text-sm text-text-mute mb-3">¿No recibiste el correo?</p>
                <button
                    onClick={handleResend}
                    disabled={resendCooldown > 0 || sendingEmail}
                    className="inline-flex items-center gap-2 px-6 py-3 border-[1.5px] border-brand text-daisies rounded-card font-semibold hover:bg-daisies/5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {sendingEmail ? (
                        <>
                            <Loader className="animate-spin" size={16} />
                            Enviando...
                        </>
                    ) : resendCooldown > 0 ? (
                        <>
                            <Clock size={16} />
                            Reenviar en {resendCooldown}s
                        </>
                    ) : (
                        <>
                            <RefreshCw size={16} />
                            Reenviar correo
                        </>
                    )}
                </button>

                {/* Escape hatch: forzar reenvío rompiendo el cooldown si nada funciona */}
                {resendCooldown > 0 && !sendingEmail && (
                    <div className="mt-3">
                        <button
                            onClick={handleForceResend}
                            className="text-xs text-text-mute underline hover:text-daisies transition-colors"
                        >
                            Forzar reenvío ahora
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmailVerification;
